/**
 * F5, a reopened thread, or an event log older than an hour: `GET /state`.
 *
 * **The stored board is ALREADY an A2UI document** (§2.1) — `values.board` is
 * `{rev, surface: {components, dataModel}}` — so this is not a translation. It
 * is one literal: the components keyed by id become the list `createSurface`
 * takes, and nothing about ids, props or children is touched on the way. A
 * translator here would be a second implementation of the layout and the order,
 * which is the drift the whole migration exists to remove.
 *
 * `rev` rides OUTSIDE the surface, in the envelope's place, because it settles
 * the one race this path has: `/state` answering with an older document than the
 * live frames that already arrived.
 */

import { CATALOG_ID } from '../catalog/generated/schemas';
import type { A2uiComponent, CreateSurfaceMessage } from './types';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export type RestoredSurface = { message: CreateSurfaceMessage; rev: number };

/** Deterministic and shared with the backend: `peterhof:board:<thread_id>`. */
export function surfaceIdFor(threadId: string): string {
  return `peterhof:board:${threadId}`;
}

/**
 * Wraps `values.board.surface` in a `createSurface`, or returns `null` when the
 * graph has no board at all (the older ones, reached through `?graph=`).
 */
export function createSurfaceFromState(
  values: Record<string, unknown>,
  threadId: string,
): RestoredSurface | null {
  const board = values.board;
  if (!isObject(board)) return null;
  const surface = board.surface;
  if (!isObject(surface)) return null;
  const stored = surface.components;
  if (!isObject(stored)) return null;

  const components = Object.values(stored).filter(
    (node): node is A2uiComponent =>
      isObject(node) && typeof node.id === 'string' && typeof node.component === 'string',
  );
  if (components.length === 0) return null;

  const dataModel = isObject(surface.dataModel) ? surface.dataModel : {};
  // The stored document need not repeat it, so the generated constant is the
  // fallback — it IS the catalogue's public URL, and `docs/A2UI_BINDING.md`
  // spells this wrapper out the same way.
  const catalogId = typeof surface.catalogId === 'string' ? surface.catalogId : CATALOG_ID;

  return {
    rev: typeof board.rev === 'number' ? board.rev : 0,
    message: {
      version: 'v1.0',
      createSurface: {
        surfaceId: surfaceIdFor(threadId),
        catalogId,
        components,
        dataModel,
      },
    },
  };
}
